import { dataPathBuilder } from './DataPathBuilder';

export interface TriggerEdgeLike {
  nextScreenId?: string;
  transitionKind?: string;
  conditions?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

export interface TriggerLike {
  kind?: string;
  edges: TriggerEdgeLike[];
  onCancelAction?: TriggerLike;
  [key: string]: unknown;
}

export interface TriggerVisitContext {
  trigger: TriggerLike;
  edgeIndex: number;
  actionKey?: string;
}

export interface TriggerEdgeRewriteSummary {
  visitedTriggers: number;
  visitedEdges: number;
  rewrittenEdges: number;
  droppedEdges: number;
  conditionKeysRewritten: number;
}

export type TriggerEdgeRewriteResult = TriggerEdgeLike | null | undefined;

export function walkTriggers(node: unknown, visit: (trigger: TriggerLike, actionKey?: string) => void): void {
  walkTriggersInternal(node, visit, new WeakSet<object>(), []);
}

export function collectTriggerEdges(root: unknown): TriggerEdgeLike[] {
  const edges: TriggerEdgeLike[] = [];
  walkTriggers(root, (trigger) => {
    for (const edge of trigger.edges) {
      if (edge && typeof edge === 'object') {
        edges.push(edge);
      }
    }
  });
  return edges;
}

export function rewriteTriggerEdgesInPlace(
  root: unknown,
  rewrite: (edge: TriggerEdgeLike, context: TriggerVisitContext) => TriggerEdgeRewriteResult,
): TriggerEdgeRewriteSummary {
  const summary: TriggerEdgeRewriteSummary = {
    visitedTriggers: 0,
    visitedEdges: 0,
    rewrittenEdges: 0,
    droppedEdges: 0,
    conditionKeysRewritten: 0,
  };

  walkTriggers(root, (trigger, actionKey) => {
    summary.visitedTriggers += 1;
    const rewritten: TriggerEdgeLike[] = [];

    trigger.edges.forEach((edge, edgeIndex) => {
      if (!edge || typeof edge !== 'object') return;

      summary.visitedEdges += 1;
      const next = rewrite(edge, { trigger, edgeIndex, actionKey });
      if (next === null) {
        summary.droppedEdges += 1;
        return;
      }

      const resolved = next ?? edge;
      if (resolved !== edge || resolved.nextScreenId !== edge.nextScreenId) {
        summary.rewrittenEdges += 1;
      }
      rewritten.push(resolved);
    });

    trigger.edges = rewritten;
  });

  return summary;
}

export function remapTriggerTargetIdsInPlace(
  root: unknown,
  idMap: Map<string, string> | Record<string, string>,
  options: {
    dropUnmapped?: boolean;
    remapConditionKeys?: boolean;
  } = {},
): TriggerEdgeRewriteSummary {
  const map = toMap(idMap);
  const summary = rewriteTriggerEdgesInPlace(root, (edge) => {
    const target = edge.nextScreenId;
    if (!target) return edge;

    const mappedTarget = map.get(target);
    if (!mappedTarget) {
      return options.dropUnmapped ? null : edge;
    }

    return {
      ...edge,
      nextScreenId: mappedTarget,
    };
  });

  if (options.remapConditionKeys) {
    summary.conditionKeysRewritten += remapConditionKeysInPlace(root, map);
  }

  return summary;
}

export function remapConditionKeysInPlace(
  root: unknown,
  idMap: Map<string, string> | Record<string, string>,
): number {
  const map = toMap(idMap);
  let rewritten = 0;

  walkTriggers(root, (trigger) => {
    for (const edge of trigger.edges) {
      if (!Array.isArray(edge.conditions)) continue;

      for (const condition of edge.conditions) {
        if (!condition || typeof condition.key !== 'string') continue;
        const nextKey = remapConditionKey(condition.key, map);
        if (nextKey === condition.key) continue;

        condition.key = nextKey;
        rewritten += 1;
      }
    }
  });

  return rewritten;
}

export function remapConditionKey(
  conditionKey: string,
  idMap: Map<string, string> | Record<string, string>,
): string {
  const parsed = dataPathBuilder.parsePath(conditionKey);
  if (!parsed?.screenId) return conditionKey;

  const mappedScreenId = toMap(idMap).get(parsed.screenId);
  if (!mappedScreenId) return conditionKey;

  return conditionKey.replace(parsed.screenId, mappedScreenId);
}

// ---------------------------------------------------------------------------
// First-match-wins shadowing (D-W6-4 / D-W6-6)
// ---------------------------------------------------------------------------

/**
 * An edge with no guards always matches
 * (`V2RouteEvaluator.evaluateRoute`: "Empty conditions = unconditional").
 * `conditions` absent and `conditions: []` are the same thing — the compiled
 * flagship carries both shapes on the same trigger.
 */
export function isUnconditionalTriggerEdge(edge: TriggerEdgeLike): boolean {
  return !Array.isArray(edge.conditions) || edge.conditions.length === 0;
}

/**
 * Indexes of the edges of ONE trigger that the V2 engine can never take.
 *
 * `V2RouteEvaluator.findMatchingRoute` sorts the edges conditional-first /
 * unconditional-last (stable within each group) and returns the FIRST whose
 * guards all pass; an unconditional edge always passes. So a conditional edge
 * is always potentially live wherever it sits in the array, and among the
 * unconditional edges only the FIRST (in array order — the sort is stable) can
 * ever be taken. Every later unconditional edge on the same trigger is dead
 * code, and anything reachable only through it is unreachable for every
 * visitor.
 *
 * Deliberately NOT covered here: the engine also ignores the edges of a
 * `kind: 'complete'` trigger outright (defect #4a). That is a different engine
 * fact with its own owner — `makeTriggerNavigable` below fixes it at write
 * time and `triggers.completeWithEdges` reports it — and folding it in would
 * change what an existing check counts.
 */
export function shadowedTriggerEdgeIndexes(trigger: TriggerLike): number[] {
  const shadowed: number[] = [];
  let liveDefaultSeen = false;
  trigger.edges.forEach((edge, index) => {
    if (!edge || typeof edge !== 'object' || !isUnconditionalTriggerEdge(edge)) return;
    if (liveDefaultSeen) {
      shadowed.push(index);
      return;
    }
    liveDefaultSeen = true;
  });
  return shadowed;
}

export interface ShadowedTriggerEdge {
  /** Dotted path of the carrying trigger, e.g. `actionBar.primary.action`. */
  actionKey?: string;
  edgeIndex: number;
  nextScreenId?: string;
}

/**
 * Every dead edge under a content root (a screen `content`, or a whole
 * document). Reachability walkers subtract these so a shadowed edge can never
 * make a screen look reachable (D-W6-6).
 */
export function collectShadowedTriggerEdges(root: unknown): ShadowedTriggerEdge[] {
  const dead: ShadowedTriggerEdge[] = [];
  walkTriggers(root, (trigger, actionKey) => {
    for (const edgeIndex of shadowedTriggerEdgeIndexes(trigger)) {
      const edge = trigger.edges[edgeIndex];
      dead.push({
        ...(actionKey === undefined ? {} : { actionKey }),
        edgeIndex,
        ...(typeof edge.nextScreenId === 'string' ? { nextScreenId: edge.nextScreenId } : {}),
      });
    }
  });
  return dead;
}

/**
 * Whether the trigger that will carry an appended edge is TERMINAL: it exists
 * and has NO EDGES, so the engine finishes the funnel there. That is the screen
 * where the visitor actually stops — the block's exit when the block graph has
 * no leaf (D-W6-4).
 *
 * The engine's own terminal condition is WIDER —
 * `trigger.kind === 'complete' || trigger.edges.length === 0`
 * (`V2NavigationEngine`) — and this predicate DELIBERATELY implements only the
 * second half (W6-fix review round 1, ruling R-W6fix-5). A `complete` trigger
 * that already carries edges is defect #4a, owned by `makeTriggerNavigable`
 * (which flips it to `advance` at write time) and reported by
 * `triggers.completeWithEdges`; treating it as an exit CANDIDATE here would
 * make the compiler pick a screen whose existing edges another owner is about
 * to make live, and would move what that check counts. An edge-less `complete`
 * is covered by `edges.length === 0`, which is the shape D-W6-4 is about.
 */
export function hasTerminalPreferredTrigger(content: unknown): boolean {
  const trigger = findPreferredTrigger(content);
  return Boolean(trigger) && (trigger as TriggerLike).edges.length === 0;
}

/**
 * Where an appended edge actually landed. Callers that compile block
 * boundaries surface `synthesized-primary` as a warning: the source screen
 * offered no advancing trigger of its own and the carrier had to be created.
 * `shadowed` is the same class of announcement for the D-W6-4 shape: the edge
 * WAS appended (dropping a declared boundary edge silently would be a worse
 * defect) but it landed behind an unconditional sibling and can never be
 * taken. It outranks `action-key` / `preferred` — those say WHICH carrier was
 * chosen, this says the append did not achieve anything — and never co-occurs
 * with `synthesized-primary` (a freshly synthesized carrier has no edges).
 * `duplicate` still wins: there nothing was appended at all.
 */
export type TriggerEdgeAppendOutcome =
  | 'action-key'
  | 'preferred'
  | 'synthesized-primary'
  | 'duplicate'
  | 'shadowed';

export function appendTriggerEdgeInPlace(
  content: Record<string, unknown>,
  edge: TriggerEdgeLike,
): TriggerEdgeAppendOutcome {
  return appendTriggerEdgeAtActionKeyInPlace(content, undefined, edge);
}

export function appendTriggerEdgeAtActionKeyInPlace(
  content: Record<string, unknown>,
  actionKey: string | undefined,
  edge: TriggerEdgeLike,
  options: { unique?: boolean } = {},
): TriggerEdgeAppendOutcome {
  const explicit = actionKey ? findTriggerByActionKey(content, actionKey) : null;
  const preferred = explicit ?? findPreferredTrigger(content);
  const trigger = preferred ?? ensurePrimaryActionTrigger(content);
  // unique: multi-exit boundary replication + recompiles of lifted documents
  // must be idempotent — an identical edge on the carrier is never doubled.
  if (options.unique && trigger.edges.some((candidate) => isSameTriggerEdge(candidate, edge))) {
    return 'duplicate';
  }
  // D-W6-4: decided BEFORE the push, on the carrier as it stands. An
  // unconditional edge appended to a carrier that already has one can never be
  // taken (see `shadowedTriggerEdgeIndexes`).
  const isShadowed = isUnconditionalTriggerEdge(edge)
    && trigger.edges.some((candidate) => (
      candidate && typeof candidate === 'object' && isUnconditionalTriggerEdge(candidate)
    ));
  trigger.edges.push(edge);
  makeTriggerNavigable(trigger);
  if (isShadowed) return 'shadowed';
  if (explicit) return 'action-key';
  return preferred ? 'preferred' : 'synthesized-primary';
}

function isSameTriggerEdge(a: TriggerEdgeLike, b: TriggerEdgeLike): boolean {
  if (a.nextScreenId !== b.nextScreenId) return false;
  if ((a.transitionKind ?? 'push') !== (b.transitionKind ?? 'push')) return false;
  return JSON.stringify(a.conditions ?? []) === JSON.stringify(b.conditions ?? []);
}

/**
 * An appended edge must be navigable: the V2 engine treats `kind:'complete'`
 * as FunnelExited and IGNORES its edges, so a `complete` trigger that just
 * gained an edge is flipped to `advance` (defect #4a — compiled block-final
 * triggers shipped as complete+edges and stranded users at block boundaries).
 */
function makeTriggerNavigable(trigger: TriggerLike): void {
  if (trigger.kind === 'complete' && trigger.edges.length > 0) {
    trigger.kind = 'advance';
  }
}

/**
 * Defect #4b: a terminal block's exit screen must carry an explicit
 * `complete` action — otherwise it compiles as a dead-end (button does
 * nothing, user stranded). Ensures the primary action trigger exists and
 * marks it terminal, but NEVER touches a screen that already navigates
 * (any existing trigger with edges is left untouched).
 */
export function ensureTerminalActionInPlace(
  content: Record<string, unknown>,
): 'stamped' | 'already-terminal' | 'has-edges' {
  const existing = findPreferredTrigger(content);
  if (existing) {
    if (existing.edges.length > 0) return 'has-edges';
    if (existing.kind === 'complete') return 'already-terminal';
    // Edge-less non-complete trigger is equally a dead-end — make it a
    // definite funnel completion instead of a dead button.
    existing.kind = 'complete';
    return 'stamped';
  }
  const trigger = ensurePrimaryActionTrigger(content);
  trigger.kind = 'complete';
  return 'stamped';
}

/** The V2 action kind that moves the visitor BACKWARD (header back button). */
const RETREAT_TRIGGER_KIND = 'retreat';
/** Canonical advance carrier path, relative to a screen `content` root. */
const PRIMARY_ACTION_KEY = 'actionBar.primary.action';
/** Any trigger nested under a header back affordance, whatever its kind. */
const BACK_ACTION_KEY_PATTERN = /(^|\.)header\.back(\.|$)/;

/**
 * A retreat trigger can never carry an ADVANCING edge: the V2 engine pops the
 * history stack for `retreat`, so the edge target is never entered and the
 * screen is a dead end for every forward route.
 */
function isRetreatTrigger(trigger: TriggerLike, actionKey?: string): boolean {
  if (trigger.kind === RETREAT_TRIGGER_KIND) return true;
  return typeof actionKey === 'string' && BACK_ACTION_KEY_PATTERN.test(actionKey);
}

/**
 * D-T9a-5: pick the trigger that will carry an appended (advancing) edge.
 *
 * The previous implementation returned the FIRST trigger in `Object.entries`
 * order. Compiled strategy screens frequently serialize `header` before
 * `actionBar`, so a block-boundary edge landed on `header.back.action`
 * (kind `retreat`): the exit screen dead-ended and the next block — on the
 * WP7 flagship, two of three paywalls — became unreachable. The strategy
 * healthcheck reported 0 errors because the defect only exists after compile.
 *
 * Preference order, retreat triggers excluded at every step:
 *   1. `actionBar.primary.action` — the canonical advance carrier;
 *   2. the first remaining non-retreat trigger (list options, secondary, …);
 *   3. none → the caller synthesizes an explicit primary action.
 */
function findPreferredTrigger(root: unknown): TriggerLike | null {
  let primary: TriggerLike | null = null;
  let firstAdvancing: TriggerLike | null = null;

  walkTriggers(root, (trigger, actionKey) => {
    if (isRetreatTrigger(trigger, actionKey)) return;
    if (!primary && actionKey === PRIMARY_ACTION_KEY) primary = trigger;
    if (!firstAdvancing) firstAdvancing = trigger;
  });

  return primary ?? firstAdvancing;
}

function findTriggerByActionKey(root: unknown, actionKey: string): TriggerLike | null {
  let match: TriggerLike | null = null;

  walkTriggers(root, (trigger, currentActionKey) => {
    if (!match && currentActionKey === actionKey) {
      match = trigger;
    }
  });

  return match;
}

function ensurePrimaryActionTrigger(content: Record<string, unknown>): TriggerLike {
  const actionBar = ensureRecord(content, 'actionBar');
  const primary = ensureRecord(actionBar, 'primary');
  const action = ensureRecord(primary, 'action') as TriggerLike;

  if (typeof action.kind !== 'string') {
    action.kind = 'advance';
  }
  if (!Array.isArray(action.edges)) {
    action.edges = [];
  }

  return action;
}

function ensureRecord(parent: Record<string, unknown>, key: string): Record<string, unknown> {
  const value = parent[key];
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  const next: Record<string, unknown> = {};
  parent[key] = next;
  return next;
}

function walkTriggersInternal(
  node: unknown,
  visit: (trigger: TriggerLike, actionKey?: string) => void,
  seen: WeakSet<object>,
  path: string[],
): void {
  if (!node || typeof node !== 'object') return;
  if (seen.has(node)) return;
  seen.add(node);

  if (Array.isArray(node)) {
    for (const [index, item] of node.entries()) {
      walkTriggersInternal(item, visit, seen, [...path, String(index)]);
    }
    return;
  }

  const obj = node as Record<string, unknown>;
  if (typeof obj.kind === 'string' && Array.isArray(obj.edges)) {
    const trigger = obj as unknown as TriggerLike;
    visit(trigger, path.join('.'));
  }

  for (const [key, value] of Object.entries(obj)) {
    walkTriggersInternal(value, visit, seen, [...path, key]);
  }
}

function toMap(input: Map<string, string> | Record<string, string>): Map<string, string> {
  return input instanceof Map ? input : new Map(Object.entries(input));
}
